import { ServiceError, unsupported } from '../errors'
import { pagosService } from '../pagos'
import { fetchPendingToPaid, pmsDateToIso } from '../pagos/pendingToPaid'
import type { ApiPendingToPaid } from '../pagos/pendingToPaid'
import { parseMoney } from '../types'
import type { Deposit, DepositKind, DepositTimelineItem, FianzaService } from './types'

/** Plazo del banco que da por hecho el diseño (D7). Solo lo usa el mock: con la API actual no se promete (fase 4, A3). */
export const BANK_DELAY_DAYS = { min: 3, max: 10 }

/** El PMS devuelve el nombre visible del tipo de pago, no un enum estable. */
export function depositKindFromPaidType(name: string | undefined): DepositKind {
  const normalized = (name ?? '').trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('es')
  if (normalized === 'preautorizacion' || normalized === 'preautorizacion bancaria') return 'preauth'
  return 'unknown'
}

/**
 * Adapta E2 a la fianza. Solo tres hitos son derivables hoy (inventario D7): retenida (`bailPaymentDate`),
 * estancia terminada (fecha de salida) y liberada/devuelta (`bailRefundDate`). Revisión, deducción,
 * ventana "disponible entre" y el tipo (preautorización o cargo) no existen en la API.
 */
export function toDeposit(api: ApiPendingToPaid, ctx: { currency: string; checkOut: string; now?: Date }): Deposit | null {
  const amount = parseMoney(api.bailPrice, ctx.currency)
  if (!amount || amount.amount <= 0) return null
  const now = ctx.now ?? new Date()
  const heldAt = api.bailPaid ? pmsDateToIso(api.bailPaymentDate) : null
  const releasedAt = api.bailRefund ? pmsDateToIso(api.bailRefundDate) : null
  const stayEnded = new Date(ctx.checkOut) <= now
  const status: Deposit['status'] = api.bailRefund ? 'refunded' : api.bailPaid ? 'held' : 'pending'
  const items: DepositTimelineItem[] = [
    { code: 'held', state: api.bailPaid ? 'done' : 'future', date: heldAt, expected: null },
    { code: 'stay_ended', state: stayEnded ? 'done' : 'future', date: stayEnded ? ctx.checkOut : null, expected: stayEnded ? null : { from: ctx.checkOut } },
    { code: 'released', state: api.bailRefund ? 'done' : 'future', date: releasedAt, expected: null },
  ]
  // El primer hito no completado es el actual.
  const firstPending = items.find((it) => it.state !== 'done')
  if (firstPending) firstPending.state = 'current'
  return {
    amount,
    kind: depositKindFromPaidType(api.paidTypeBailName),
    status,
    cardLast4: null,
    heldAt,
    holdUntil: null,
    reviewDeadline: null,
    releasedAt,
    releasedAmount: api.bailRefund ? (parseMoney(api.bailToRefundPrice, ctx.currency) ?? amount) : null,
    bankDelayDays: null,
    availableWindow: null,
    timeline: items,
    deduction: null,
    claim: null,
  }
}

export const fianzaReal: FianzaService = {
  async getDeposit(stay, lang, signal) {
    const api = await fetchPendingToPaid(stay, lang, signal)
    if (!api.ok) throw new ServiceError('server', api.error ?? api.message, api.status)
    return toDeposit(api, { currency: stay.currency, checkOut: stay.dates.checkOut })
  },
  reserveDeposit(stay, amount, card, lang, signal) {
    return pagosService.pay(stay, { kind: 'deposit', amount, card }, lang, signal)
  },
  // Sin endpoint: en real no hay deducciones, así que no se llega a reclamar (D36).
  claim: () => unsupported(),
}
